import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Star, Calendar, Building2, MapPin, DollarSign, FileText,
  ExternalLink, AlertTriangle, Mail, Phone,
  RefreshCw, ChevronDown, ChevronUp, Briefcase, Clock,
  Users, Sparkles, Upload, FileCheck, Loader2, Eye
} from 'lucide-react';
import { Contract } from '../types';
import { useContractActions } from '../hooks/useContractActions';
import { ContractMatcher, ScoreBreakdown, DetailedScore } from '../lib/matching/ContractMatcher';
import { VerdictBlock } from './ui/VerdictBlock';
import { FactorBreakdown, ScoreFactor } from './ui/FactorBreakdown';
import { fitLevel, FIT_VERDICT } from '../lib/fit';
import { useAuth } from '../contexts/AuthContext';
import { PIPELINE_STAGES, STAGE_META, setContractStage, type PipelineStage } from '../lib/pipeline-service';
import { useContractEnrichment } from '../hooks/useContractEnrichment';
import { useContractDocuments } from '../hooks/useContractDocuments';
import { useContractDocumentUpload } from '../hooks/useContractDocumentUpload';
import { supabase } from '../lib/supabase';
import { DocumentAnalysisPanel } from './DocumentAnalysisPanel';
import { EnhancedBidWinScorePanel } from './EnhancedBidWinScorePanel';
import { AIVerdictCard } from './ui/AIVerdictCard';
import { DocumentAnalysisResult } from '../hooks/useDocumentAnalysis';
import toast from 'react-hot-toast';

interface ContractSlideoutProps {
  contract: Contract;
  onClose: () => void;
  onGenerateProposal: () => void;
  onViewProposal?: () => void;
  hasProposal?: boolean;
}

export function ContractSlideout({
  contract,
  onClose,
  onGenerateProposal,
  onViewProposal,
  hasProposal
}: ContractSlideoutProps) {
  const { profile } = useAuth();
  const { loading: actionLoading, toggleFavorite } = useContractActions();
  const { loading: enriching, enrichContract, needsEnrichment } = useContractEnrichment();

  const [enrichedContract, setEnrichedContract] = useState<Contract>(contract);
  const [isEnriched, setIsEnriched] = useState(false);
  // Reveal the AI Analysis section by default when an AI verdict already exists.
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(contract.aiScored ? ['analysis'] : [])
  );
  const [analysisResult, setAnalysisResult] = useState<DocumentAnalysisResult | null>(null);
  const [analysisRefreshKey, setAnalysisRefreshKey] = useState(0);
  const [detailedScore, setDetailedScore] = useState<DetailedScore | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [fetchingSamDocs, setFetchingSamDocs] = useState(false);
  const [stage, setStage] = useState<PipelineStage | undefined>(contract.pipelineStage);
  const [savingStage, setSavingStage] = useState(false);
  // The AI capture-analyst verdict, seeded from the contract prop and refreshed
  // in place when the user re-scores from this modal.
  const [aiVerdict, setAiVerdict] = useState<Partial<Contract> | null>(null);
  const [rescoringAi, setRescoringAi] = useState(false);
  const aiContract: Contract = aiVerdict ? { ...contract, ...aiVerdict } : contract;

  const handleSetStage = async (s: PipelineStage) => {
    if (!profile) return;
    const next = s === stage ? null : s; // tapping the active stage clears it
    setStage(next ?? undefined);
    setSavingStage(true);
    try {
      await setContractStage(profile.id, contract.notice_id || contract.id, next);
    } catch (e) {
      console.error('Failed to set pipeline stage:', e);
    } finally {
      setSavingStage(false);
    }
  };

  // Fetch uploaded contract documents
  const noticeId = contract.notice_id || contract.id;
  const {
    documents: uploadedDocs,
    loading: docsLoading,
    refresh: refreshDocs,
    getDocumentsSummary
  } = useContractDocuments({
    noticeId,
    includeExtractedText: true,
  });

  // Get document stats
  const docsSummary = getDocumentsSummary();
  const hasUploadedDocs = uploadedDocs.length > 0;
  const completedDocs = uploadedDocs.filter(d => d.status === 'completed');
  const pendingDocs = uploadedDocs.filter(d => d.status === 'pending' || d.status === 'processing');

  // Manual upload (for docs SAM.gov can't serve — e.g. PIEE/CAC-gated DoD files).
  const { uploadingFiles, handleFileUpload } = useContractDocumentUpload(noticeId, refreshDocs);

  // Poll contract_documents until text extraction finishes for this notice.
  // fetch-sam-documents chains extract-document-text asynchronously, so the
  // attachments land 'pending'/'processing' before any text is ready to analyze.
  // Returns true once at least one doc is 'completed' and none are still in
  // flight, false if everything failed; bounded so the import can't hang.
  const waitForExtraction = useCallback(async (): Promise<boolean> => {
    const deadline = Date.now() + 90_000;
    while (Date.now() < deadline) {
      const { data } = await supabase
        .from('contract_documents')
        .select('status')
        .eq('contract_id', noticeId);
      const docs = data || [];
      if (docs.length > 0) {
        const inFlight = docs.filter((d: any) => d.status === 'pending' || d.status === 'processing');
        const completed = docs.filter((d: any) => d.status === 'completed');
        if (inFlight.length === 0) return completed.length > 0;
      }
      await new Promise((r) => setTimeout(r, 4000));
    }
    const { data } = await supabase
      .from('contract_documents')
      .select('status')
      .eq('contract_id', noticeId);
    return (data || []).some((d: any) => d.status === 'completed');
  }, [noticeId]);

  // Pull the public solicitation attachments straight from SAM.gov for this
  // notice (download -> store -> extract text -> RAG index), wait for extraction,
  // then run requirements analysis — each stage observable, with real errors.
  // Saves the user from manual upload and guarantees the analysis actually runs.
  const handleFetchFromSam = useCallback(async () => {
    const tid = 'sam-import';
    setFetchingSamDocs(true);
    try {
      toast.loading('Fetching documents from SAM.gov…', { id: tid });
      const { data, error } = await supabase.functions.invoke('fetch-sam-documents', {
        body: { contract_id: noticeId },
      });
      if (error) throw error;

      const downloaded = data?.downloaded ?? 0;
      const existing = data?.skipped_exists ?? 0;
      if (data?.links_found === 0) {
        toast('No downloadable attachments on SAM.gov for this notice.', { id: tid });
        return;
      }
      if (downloaded === 0 && existing === 0) {
        toast(
          data?.skipped_access
            ? 'Documents require SAM.gov access we don’t have.'
            : 'No new documents fetched.',
          { id: tid }
        );
        await refreshDocs();
        return;
      }

      toast.loading('Extracting text from documents…', { id: tid });
      await refreshDocs();
      const ready = await waitForExtraction();
      await refreshDocs();
      if (!ready) {
        toast(
          'Documents fetched — text extraction is still running. Use “Analyze” shortly.',
          { id: tid }
        );
        return;
      }

      toast.loading('Analyzing requirements…', { id: tid });
      const { error: aErr } = await supabase.functions.invoke('analyze-contract-documents', {
        body: { contractId: noticeId },
      });
      if (aErr) throw aErr;

      setExpandedSections((prev) => new Set(prev).add('analysis'));
      setAnalysisRefreshKey((k) => k + 1);
      await refreshDocs();
      toast.success('Documents imported and analyzed', { id: tid });
    } catch (e) {
      console.error('Fetch from SAM.gov failed:', e);
      toast.error('Failed to import documents from SAM.gov', { id: tid });
    } finally {
      setFetchingSamDocs(false);
    }
  }, [noticeId, refreshDocs, waitForExtraction]);

  // Re-run the AI capture-analyst on just this contract, on demand, and refresh
  // the verdict in place. Targets one notice (cheap, ~1 Haiku call) so the user
  // can update the score from the solicitation view after importing documents or
  // changing their profile.
  const handleAiRescore = useCallback(async () => {
    if (!profile) return;
    setRescoringAi(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-match-contracts', {
        body: { user_id: profile.id, notice_ids: [noticeId], force: true },
      });
      if (error) throw error;
      if (data && data.success === false) throw new Error(data.error || 'Scoring failed');

      const { data: m } = await supabase
        .from('ai_contract_matches')
        .select('fit_score, tier, eligibility, rationale, recommended_action, blockers, opportunities')
        .eq('user_id', profile.id)
        .eq('contract_id', noticeId)
        .maybeSingle();

      // A row with an eligibility verdict OR a rationale IS a valid result —
      // even when the score is 0 (e.g. the AI judged it ineligible or a closed
      // award). Only a genuinely empty/absent row means "couldn't score".
      const hasVerdict = !!m && (
        (typeof m.fit_score === 'number' && m.fit_score > 0) ||
        (!!m.eligibility && m.eligibility !== '') ||
        (!!m.rationale && String(m.rationale).trim() !== '')
      );
      if (hasVerdict) {
        setAiVerdict({
          matchScore: typeof m!.fit_score === 'number' ? m!.fit_score : 0,
          aiScored: true, // surface the verdict in the card, incl. ineligible/0
          aiTier: m!.tier ?? undefined,
          aiEligibility: m!.eligibility ?? undefined,
          aiRationale: m!.rationale ?? undefined,
          aiRecommendedAction: m!.recommended_action ?? undefined,
          aiBlockers: Array.isArray(m!.blockers) ? m!.blockers : undefined,
          aiOpportunities: Array.isArray(m!.opportunities) ? m!.opportunities : undefined,
        });
        setExpandedSections((prev) => new Set(prev).add('analysis'));
        toast.success(
          m!.eligibility === 'ineligible'
            ? 'AI assessed this as ineligible — see the reasoning'
            : 'AI fit re-scored'
        );
      } else {
        toast('AI could not produce a score for this contract.');
      }
    } catch (e) {
      console.error('AI re-score failed:', e);
      toast.error('Failed to re-score with AI');
    } finally {
      setRescoringAi(false);
    }
  }, [profile, noticeId]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Auto-enrich if needed
  useEffect(() => {
    let mounted = true;

    async function autoEnrich() {
      const noticeId = contract.notice_id || contract.id;
      if (needsEnrichment(contract) && !isEnriched) {
        const result = await enrichContract(noticeId);
        if (mounted && result.success) {
          const { data: updated } = await supabase
            .from('sam_contracts')
            .select('*')
            .eq('notice_id', noticeId)
            .single();

          if (updated) {
            setEnrichedContract({ ...contract, ...updated });
          }
          setIsEnriched(true);
        }
      }
    }

    autoEnrich();
    return () => { mounted = false; };
  }, [contract, needsEnrichment, enrichContract, isEnriched]);

  // Calculate score breakdown
  useEffect(() => {
    if (profile) {
      const score = ContractMatcher.getDetailedScore(contract, profile);
      setDetailedScore(score);
    }
  }, [contract, profile]);

  const handleAnalysisComplete = useCallback((result: DocumentAnalysisResult) => {
    setAnalysisResult(result);
    if (profile && result.requirements.length > 0) {
      const enhancedScore = ContractMatcher.getEnhancedDetailedScore(contract, profile, result.requirements);
      setDetailedScore(enhancedScore);
    }
  }, [profile, contract]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const handleRefreshData = async () => {
    const result = await enrichContract(enrichedContract.notice_id || enrichedContract.id, true);
    if (result.success) {
      const { data: updated } = await supabase
        .from('sam_contracts')
        .select('*')
        .eq('notice_id', enrichedContract.notice_id || enrichedContract.id)
        .single();

      if (updated) {
        setEnrichedContract({ ...contract, ...updated });
        toast.success('Contract data refreshed');
      }
    }
  };

  // Format date helper
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Format file size helper
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get text preview for a document
  const getTextPreview = (text: string | null): string => {
    if (!text) return '';
    const cleaned = text.replace(/\s+/g, ' ').trim();
    if (cleaned.length <= 300) return cleaned;
    return cleaned.substring(0, 300) + '...';
  };

  // Get word count for a document
  const getWordCount = (text: string | null): number => {
    if (!text) return 0;
    return text.split(/\s+/).filter(Boolean).length;
  };

  // Calculate days until due
  const getDaysUntilDue = () => {
    if (!contract.dueDate) return null;
    const now = new Date();
    const due = new Date(contract.dueDate);
    const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const daysUntilDue = getDaysUntilDue();

  // Get documents
  const documents = (enrichedContract as any).metadata?.documents || (contract as any).metadata?.documents || [];

  // ── Verdict + factors (rendered straight from whatever the engine returns) ──
  const score = contract.matchScore || 0;
  const level = fitLevel(score);
  const hasBreakdown = !!detailedScore && detailedScore.breakdown.length > 0;
  const factors: ScoreFactor[] = (detailedScore?.breakdown || []).map((b: ScoreBreakdown) => ({
    category: b.category,
    score: b.score,
    weight: b.weight,
    description: b.description,
  }));
  const sortedByScore = [...factors].sort((a, b) => b.score - a.score);
  const topDriver = sortedByScore[0];
  const topGap = sortedByScore[sortedByScore.length - 1];
  const aiVerified = !!analysisResult;
  const judgment = hasBreakdown
    ? FIT_VERDICT[level]
    : 'Complete your profile to score this opportunity';
  const honestyLine = hasBreakdown
    ? `Scored on ${factors
        .slice(0, 3)
        .map((f) => f.category.replace(/ (Match|Eligibility|Alignment|Experience|Data)$/, ''))
        .join(', ')}${
        aiVerified
          ? ' + AI document analysis.'
          : '. Run AI deep-score for full capabilities analysis.'
      }`
    : 'Add your NAICS codes, certifications, and capabilities for a personalized score.';
  const handleDeepScore = () => {
    setExpandedSections((prev) => new Set(prev).add('analysis'));
    setTimeout(() => {
      document.getElementById('bw-ai-analysis')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slideout Panel */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[440px] md:w-[470px] bg-bw-surface shadow-2xl z-50 flex flex-col overflow-hidden animate-slide-in-right">
        {/* Red urgency strip when closing soon */}
        {daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 5 && (
          <div className="flex-shrink-0 px-4 py-2 bg-urgent-bg border-b border-urgent-border">
            <span className="text-[11.5px] text-urgent font-semibold flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {daysUntilDue === 0
                ? 'Closes today — review urgently'
                : `Closes in ${daysUntilDue} day${daysUntilDue === 1 ? '' : 's'} — review urgently`}
            </span>
          </div>
        )}

        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-bw-border bg-bw-surface">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-bw-muted tracking-[0.1em] uppercase px-1.5 py-0.5 bg-bw-surface-subtle rounded">
              {contract.status?.toUpperCase() || 'OPEN'}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => toggleFavorite(contract.id)}
                disabled={actionLoading}
                aria-label={contract.isFavorited ? 'Remove from saved' : 'Save contract'}
                className={`p-1.5 rounded-lg transition-all ${
                  contract.isFavorited
                    ? 'text-fit-moderate-stroke bg-fit-moderate-bg'
                    : 'text-bw-muted hover:bg-bw-surface-subtle'
                }`}
              >
                <Star className="h-[18px] w-[18px]" fill={contract.isFavorited ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={handleRefreshData}
                disabled={enriching}
                aria-label="Refresh contract data"
                className="p-1.5 rounded-lg text-bw-muted hover:bg-bw-surface-subtle transition-all disabled:opacity-50"
              >
                <RefreshCw className={`h-[18px] w-[18px] ${enriching ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                aria-label="Close panel"
                className="p-1.5 rounded-lg text-bw-muted hover:bg-bw-surface-subtle transition-all"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
          <h2 className="text-[15px] font-bold text-bw-ink leading-snug line-clamp-2 mb-1.5">
            {contract.title}
          </h2>
          <div className="flex items-center gap-2 text-[11px] text-bw-muted flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              {contract.agency}
            </span>
            <span className="font-mono px-1.5 py-0.5 bg-bw-surface-subtle rounded">NAICS {contract.naicsCode}</span>
          </div>

          {/* Pipeline stage selector — move this opportunity through the capture lifecycle */}
          <div className="mt-2.5 flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-bold text-bw-muted uppercase tracking-wide mr-1">Pipeline</span>
            {PIPELINE_STAGES.map((s) => {
              const active = stage === s;
              return (
                <button
                  key={s}
                  onClick={() => handleSetStage(s)}
                  disabled={savingStage}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border transition-all disabled:opacity-50 ${
                    active ? STAGE_META[s].pill : 'border-bw-border text-bw-muted hover:bg-bw-surface-subtle'
                  }`}
                >
                  {active && <span className={`h-1.5 w-1.5 rounded-full ${STAGE_META[s].dot}`} />}
                  {STAGE_META[s].label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-3">
            {/* Verdict first, then the factor drill-down (collapsed) */}
            <VerdictBlock
              score={score}
              judgment={judgment}
              driver={topDriver?.description}
              gap={topGap && topDriver && topGap.score < topDriver.score ? topGap.description : undefined}
              honestyLine={honestyLine}
              aiVerified={aiVerified}
              onDeepScore={handleDeepScore}
            />
            <FactorBreakdown factors={factors} totalScore={score} />

            {/* AI capture-analyst verdict — the smart read users see on AI Best
                Matches cards. Supersedes the rule-based score when present. */}
            {aiContract.aiScored && (
              <div className="space-y-1.5">
                <AIVerdictCard contract={aiContract} />
                <button
                  onClick={handleAiRescore}
                  disabled={rescoringAi}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-bw-primary hover:underline disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${rescoringAi ? 'animate-spin' : ''}`} />
                  {rescoringAi ? 'Re-scoring…' : 'Re-score with AI'}
                </button>
              </div>
            )}

            {/* Key Info Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-bw-surface-subtle rounded-xl p-4">
                <div className="flex items-center gap-2 text-bw-muted text-xs mb-1">
                  <Calendar className="h-4 w-4" />
                  Due Date
                </div>
                <div className="font-semibold text-bw-ink">
                  {formatDate(contract.dueDate)}
                </div>
                {daysUntilDue !== null && (
                  <div className={`text-xs mt-1 ${
                    daysUntilDue <= 7 ? 'text-urgent' :
                    daysUntilDue <= 14 ? 'text-fit-moderate' : 'text-bw-muted'
                  }`}>
                    {daysUntilDue < 0 ? 'Past due' :
                     daysUntilDue === 0 ? 'Due today!' :
                     `${daysUntilDue} days remaining`}
                  </div>
                )}
              </div>

              <div className="bg-bw-surface-subtle rounded-xl p-4">
                <div className="flex items-center gap-2 text-bw-muted text-xs mb-1">
                  <DollarSign className="h-4 w-4" />
                  Contract Value
                </div>
                <div className="font-semibold text-bw-ink">
                  {contract.fundingAmount && contract.fundingAmount > 0
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(contract.fundingAmount)
                    : 'Not specified'}
                </div>
              </div>

              {contract.placeOfPerformance && (
                <div className="bg-bw-surface-subtle rounded-xl p-4">
                  <div className="flex items-center gap-2 text-bw-muted text-xs mb-1">
                    <MapPin className="h-4 w-4" />
                    Location
                  </div>
                  <div className="font-semibold text-bw-ink text-sm">
                    {typeof contract.placeOfPerformance.city === 'object'
                      ? (contract.placeOfPerformance.city as any)?.name
                      : contract.placeOfPerformance.city}
                    {contract.placeOfPerformance.city && contract.placeOfPerformance.state ? ', ' : ''}
                    {typeof contract.placeOfPerformance.state === 'object'
                      ? (contract.placeOfPerformance.state as any)?.name
                      : contract.placeOfPerformance.state}
                  </div>
                </div>
              )}

              <div className="bg-bw-surface-subtle rounded-xl p-4">
                <div className="flex items-center gap-2 text-bw-muted text-xs mb-1">
                  <Briefcase className="h-4 w-4" />
                  NAICS Code
                </div>
                <div className="font-semibold text-bw-ink">
                  {contract.naicsCode || 'N/A'}
                </div>
              </div>
            </div>

            {/* Bid Assessment Warning */}
            {((enrichedContract as any).is_recompete || (enrichedContract as any).incumbent_contractor) && (
              <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-fit-moderate flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-fit-moderate">Incumbent Alert</h4>
                    <p className="text-sm text-fit-moderate mt-1">
                      This is a recompete.
                      {(enrichedContract as any).incumbent_contractor && (
                        <> Current incumbent: <strong>{(enrichedContract as any).incumbent_contractor}</strong></>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Collapsible Sections */}

            {/* Description Section */}
            <div className="border border-bw-border rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('description')}
                className="w-full flex items-center justify-between p-4 bg-bw-surface hover:bg-bw-surface-subtle transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-bw-primary" />
                  <span className="font-medium text-bw-ink">Description</span>
                </div>
                {expandedSections.has('description') ? (
                  <ChevronUp className="h-5 w-5 text-bw-muted" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-bw-muted" />
                )}
              </button>
              {expandedSections.has('description') && (
                <div className="p-4 bg-bw-surface">
                  {(enrichedContract.description || contract.description)?.startsWith('http') ? (
                    <div className="space-y-2">
                      <p className="text-bw-muted text-sm italic">
                        Full description available on SAM.gov
                      </p>
                      <a
                        href={`https://sam.gov/workspace/contract/opp/${enrichedContract.notice_id || contract.notice_id || contract.id}/view`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-bw-primary hover:underline text-sm"
                      >
                        View on SAM.gov <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  ) : (
                    <p className="text-bw-ink text-sm leading-relaxed whitespace-pre-line">
                      {enrichedContract.description || contract.description || 'No description available'}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Documents Section */}
            <div className="border border-bw-border rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('documents')}
                className="w-full flex items-center justify-between p-4 bg-bw-surface hover:bg-bw-surface-subtle transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-bw-primary" />
                  <span className="font-medium text-bw-ink">Documents & Links</span>
                  <span className="text-xs bg-bw-primary text-white px-2 py-0.5 rounded-full">
                    {documents.length + uploadedDocs.length}
                  </span>
                  {hasUploadedDocs && completedDocs.length > 0 && (
                    <span className="text-xs bg-success-bg text-success px-2 py-0.5 rounded-full flex items-center gap-1">
                      <FileCheck className="h-3 w-3" />
                      {completedDocs.length} extracted
                    </span>
                  )}
                </div>
                {expandedSections.has('documents') ? (
                  <ChevronUp className="h-5 w-5 text-bw-muted" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-bw-muted" />
                )}
              </button>
              {expandedSections.has('documents') && (
                <div className="p-4 bg-bw-surface space-y-4">
                  {/* Uploaded Documents Section */}
                  {hasUploadedDocs && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-bw-ink flex items-center gap-2">
                          <Upload className="h-4 w-4 text-success" />
                          Uploaded Documents ({uploadedDocs.length})
                        </h4>
                        {docsSummary.totalCharacters > 0 && (
                          <span className="text-xs text-success font-medium">
                            {docsSummary.totalCharacters.toLocaleString()} chars extracted
                          </span>
                        )}
                      </div>

                      {/* Extraction Summary Banner */}
                      {completedDocs.length > 0 && (
                        <div className="bg-success-bg border border-success-border rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <FileCheck className="h-5 w-5 text-success" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-success">
                                Text extracted from {completedDocs.length} document{completedDocs.length !== 1 ? 's' : ''}
                              </p>
                              <p className="text-xs text-success">
                                {docsSummary.totalCharacters.toLocaleString()} characters available for AI analysis
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Processing Banner */}
                      {pendingDocs.length > 0 && (
                        <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-5 w-5 text-bw-primary animate-spin" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-bw-primary">
                                Extracting text from {pendingDocs.length} document{pendingDocs.length !== 1 ? 's' : ''}...
                              </p>
                              <p className="text-xs text-bw-primary">This may take a moment</p>
                            </div>
                            <button
                              onClick={() => refreshDocs()}
                              className="text-xs text-bw-primary hover:text-bw-primary"
                            >
                              Refresh
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Uploaded Documents List */}
                      <div className="space-y-2">
                        {uploadedDocs.map((doc) => {
                          const wordCount = getWordCount(doc.raw_text);
                          const isExpanded = previewDocId === doc.id;
                          const preview = getTextPreview(doc.raw_text);

                          return (
                            <div
                              key={doc.id}
                              className={`rounded-lg border overflow-hidden ${
                                doc.status === 'completed' ? 'border-green-200 bg-success-bg/50' :
                                doc.status === 'failed' ? 'border-red-200 bg-urgent-bg/50' :
                                'border-bw-border bg-bw-surface-subtle'
                              }`}
                            >
                              <div className="flex items-center gap-3 p-3">
                                {doc.status === 'processing' || doc.status === 'pending' ? (
                                  <Loader2 className="h-5 w-5 text-bw-primary animate-spin flex-shrink-0" />
                                ) : doc.status === 'completed' ? (
                                  <FileCheck className="h-5 w-5 text-success flex-shrink-0" />
                                ) : (
                                  <AlertTriangle className="h-5 w-5 text-urgent flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-bw-ink truncate">
                                    {doc.document_name}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-bw-muted flex-wrap">
                                    <span>{formatFileSize(doc.file_size)}</span>
                                    {doc.page_count && <span>• {doc.page_count} pages</span>}
                                    {doc.status === 'completed' && wordCount > 0 && (
                                      <span className="text-success font-medium">
                                        • {wordCount.toLocaleString()} words
                                      </span>
                                    )}
                                    {doc.status === 'failed' && (
                                      <span className="text-urgent">• Extraction failed</span>
                                    )}
                                  </div>
                                </div>
                                {doc.status === 'completed' && doc.raw_text && (
                                  <button
                                    onClick={() => setPreviewDocId(isExpanded ? null : doc.id)}
                                    className="p-1.5 text-bw-muted hover:text-bw-primary hover:bg-bw-primary-tint rounded transition-colors"
                                    title="Preview extracted text"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                )}
                                <a
                                  href={doc.document_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded transition-colors"
                                  title="Open document"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </div>

                              {/* Text Preview */}
                              {isExpanded && preview && (
                                <div className="border-t border-bw-border p-3 bg-bw-surface">
                                  <div className="flex items-center gap-2 mb-2">
                                    <FileText className="h-4 w-4 text-bw-muted" />
                                    <span className="text-xs font-medium text-bw-muted">Extracted Text Preview</span>
                                  </div>
                                  <div className="text-xs text-bw-muted bg-bw-surface-subtle rounded p-3 max-h-32 overflow-y-auto font-mono whitespace-pre-wrap">
                                    {preview}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* SAM.gov Documents Section */}
                  {documents.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-bw-ink flex items-center gap-2">
                        <ExternalLink className="h-4 w-4 text-bw-primary" />
                        SAM.gov Documents ({documents.length})
                      </h4>
                      {documents.map((doc: any, index: number) => {
                        const hasValidUrl = doc.url && doc.url !== '#' && doc.url.startsWith('http');
                        const displayUrl = hasValidUrl ? doc.url : `https://sam.gov/workspace/contract/opp/${enrichedContract.notice_id || contract.notice_id || contract.id}/view`;

                        return (
                          <a
                            key={index}
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-lg bg-bw-surface-subtle hover:bg-bw-surface-subtle transition-colors"
                          >
                            <FileText className="h-5 w-5 text-bw-muted" />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-bw-ink truncate block text-sm">
                                {doc.name || 'Document'}
                              </span>
                              {doc.description && (
                                <p className="text-xs text-bw-muted truncate">{doc.description}</p>
                              )}
                            </div>
                            <ExternalLink className="h-4 w-4 text-bw-muted flex-shrink-0" />
                          </a>
                        );
                      })}
                    </div>
                  )}

                  {/* View on SAM.gov Link */}
                  <a
                    href={`https://sam.gov/workspace/contract/opp/${enrichedContract.notice_id || contract.notice_id || contract.id}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-bw-primary-tint rounded-lg hover:bg-bw-primary-tint transition-colors"
                  >
                    <ExternalLink className="h-5 w-5 text-bw-primary" />
                    <div className="flex-1">
                      <span className="font-medium text-bw-primary">View on SAM.gov</span>
                      <p className="text-xs text-bw-primary">Access all official documents and attachments</p>
                    </div>
                  </a>

                  {/* Add documents: fetch from SAM.gov (public files) OR upload your
                      own — needed when the real files live in PIEE/CAC-gated portals
                      that SAM.gov can't serve. Always available so you can re-add
                      documents anytime, not only during generation. */}
                  <div className="py-3 space-y-3">
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <button
                        onClick={handleFetchFromSam}
                        disabled={fetchingSamDocs}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-bw-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
                      >
                        {fetchingSamDocs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        {fetchingSamDocs ? 'Fetching from SAM.gov…' : (hasUploadedDocs ? 'Re-fetch from SAM.gov' : 'Fetch documents from SAM.gov')}
                      </button>
                      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-bw-border text-bw-ink text-sm font-medium hover:bg-bw-surface-subtle cursor-pointer">
                        <Upload className="h-4 w-4" />
                        Upload documents
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                          className="hidden"
                          onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-bw-muted text-center">
                      "Fetch" pulls public SAM.gov files. If the solicitation's files are in PIEE or another
                      CAC-gated portal (common for DoD), SAM.gov has nothing to pull — download them there and
                      <span className="font-medium"> upload them here</span> (PDF, Word, Excel, or text; max 50MB each).
                    </p>

                    {/* In-flight manual uploads */}
                    {uploadingFiles.length > 0 && (
                      <div className="space-y-1.5">
                        {uploadingFiles.map((uf, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            {uf.status === 'completed' ? (
                              <FileCheck className="h-4 w-4 text-success flex-shrink-0" />
                            ) : uf.status === 'failed' ? (
                              <AlertTriangle className="h-4 w-4 text-urgent flex-shrink-0" />
                            ) : (
                              <Loader2 className="h-4 w-4 text-bw-primary animate-spin flex-shrink-0" />
                            )}
                            <span className="truncate flex-1 text-bw-ink">{uf.file.name}</span>
                            <span className="text-bw-muted">
                              {uf.status === 'completed'
                                ? `${(uf.wordCount || 0).toLocaleString()} words`
                                : uf.status === 'failed'
                                ? (uf.error || 'failed')
                                : uf.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* AI Analysis Section */}
            <div id="bw-ai-analysis" className="border border-bw-border rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('analysis')}
                className="w-full flex items-center justify-between p-4 bg-bw-surface hover:bg-bw-surface-subtle transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-bw-primary" />
                  <span className="font-medium text-bw-ink">AI Analysis</span>
                  {aiContract.aiScored && (
                    <span className="text-xs bg-bw-primary-tint text-bw-primary px-2 py-0.5 rounded-full">
                      Fit scored
                    </span>
                  )}
                  {hasUploadedDocs && completedDocs.length > 0 && (
                    <span className="text-xs bg-bw-primary-tint text-bw-primary px-2 py-0.5 rounded-full">
                      {completedDocs.length} docs ready
                    </span>
                  )}
                </div>
                {expandedSections.has('analysis') ? (
                  <ChevronUp className="h-5 w-5 text-bw-muted" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-bw-muted" />
                )}
              </button>
              {expandedSections.has('analysis') && (
                <div className="p-4 bg-bw-surface space-y-4">
                  {/* AI fit verdict (the scoring reasoning) — revealed here too */}
                  {aiContract.aiScored ? (
                    <div className="space-y-1.5">
                      <AIVerdictCard contract={aiContract} />
                      <button
                        onClick={handleAiRescore}
                        disabled={rescoringAi}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-bw-primary hover:underline disabled:opacity-50"
                      >
                        <RefreshCw className={`h-3 w-3 ${rescoringAi ? 'animate-spin' : ''}`} />
                        {rescoringAi ? 'Re-scoring…' : 'Re-score with AI'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3 p-3 bg-bw-primary-tint border border-bw-primary/20 rounded-lg">
                      <p className="text-sm text-bw-primary">
                        Get an AI fit verdict for this opportunity — tailored to your profile and set-asides.
                      </p>
                      <button
                        onClick={handleAiRescore}
                        disabled={rescoringAi}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-bw-primary text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 flex-shrink-0"
                      >
                        {rescoringAi ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5" />
                        )}
                        {rescoringAi ? 'Scoring…' : 'Score with AI'}
                      </button>
                    </div>
                  )}

                  {/* Show uploaded docs info for document analysis */}
                  {hasUploadedDocs && completedDocs.length > 0 && (
                    <div className="p-3 bg-bw-primary-tint border border-bw-primary/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileCheck className="h-5 w-5 text-bw-primary" />
                        <div>
                          <p className="text-sm font-medium text-bw-primary">
                            {completedDocs.length} document{completedDocs.length !== 1 ? 's' : ''} available for requirements analysis
                          </p>
                          <p className="text-xs text-bw-primary">
                            {docsSummary.totalCharacters.toLocaleString()} characters of extracted text
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Document requirements / SOW / personnel extraction */}
                  <DocumentAnalysisPanel
                    contractId={contract.notice_id || contract.id}
                    hasDocuments={documents.length > 0 || completedDocs.length > 0}
                    onAnalysisComplete={handleAnalysisComplete}
                    refreshKey={analysisRefreshKey}
                  />
                </div>
              )}
            </div>

            {/* Contact Section */}
            {(enrichedContract.point_of_contact?.primary || contract.point_of_contact?.primary) && (
              <div className="border border-bw-border rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('contact')}
                  className="w-full flex items-center justify-between p-4 bg-bw-surface hover:bg-bw-surface-subtle transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-bw-primary" />
                    <span className="font-medium text-bw-ink">Point of Contact</span>
                  </div>
                  {expandedSections.has('contact') ? (
                    <ChevronUp className="h-5 w-5 text-bw-muted" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-bw-muted" />
                  )}
                </button>
                {expandedSections.has('contact') && (
                  <div className="p-4 bg-bw-surface">
                    <div className="space-y-3">
                      {(enrichedContract.point_of_contact?.primary || contract.point_of_contact?.primary) && (
                        <div>
                          <p className="font-medium text-bw-ink">
                            {(enrichedContract.point_of_contact?.primary || contract.point_of_contact?.primary)?.name}
                          </p>
                          <p className="text-sm text-bw-muted">
                            {(enrichedContract.point_of_contact?.primary || contract.point_of_contact?.primary)?.title}
                          </p>
                          {(enrichedContract.point_of_contact?.primary?.email || contract.point_of_contact?.primary?.email) && (
                            <a
                              href={`mailto:${enrichedContract.point_of_contact?.primary?.email || contract.point_of_contact?.primary?.email}`}
                              className="flex items-center gap-2 text-sm text-bw-primary hover:underline mt-2"
                            >
                              <Mail className="h-4 w-4" />
                              {enrichedContract.point_of_contact?.primary?.email || contract.point_of_contact?.primary?.email}
                            </a>
                          )}
                          {(enrichedContract.point_of_contact?.primary?.phone || contract.point_of_contact?.primary?.phone) && (
                            <a
                              href={`tel:${enrichedContract.point_of_contact?.primary?.phone || contract.point_of_contact?.primary?.phone}`}
                              className="flex items-center gap-2 text-sm text-bw-primary hover:underline mt-1"
                            >
                              <Phone className="h-4 w-4" />
                              {enrichedContract.point_of_contact?.primary?.phone || contract.point_of_contact?.primary?.phone}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Fixed Action Bar */}
        <div className="flex-shrink-0 border-t border-bw-border bg-bw-surface p-3">
          <div className="flex gap-2">
            <a
              href={`https://sam.gov/workspace/contract/opp/${enrichedContract.notice_id || contract.notice_id || contract.id}/view`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-[1.5px] border-bw-border rounded-lg text-bw-ink text-[12px] font-semibold hover:bg-bw-surface-subtle transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              View on SAM.gov
            </a>
            <button
              onClick={hasProposal && onViewProposal ? onViewProposal : onGenerateProposal}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-bw-primary text-white rounded-lg text-[12px] font-semibold hover:brightness-110 transition-all"
            >
              <Sparkles className="h-4 w-4" />
              {hasProposal ? 'View Proposal' : 'Generate Proposal'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
